// src/components/FilterToolbar.jsx
//
// Enterprise-class compact filter toolbar primitives.
// Designed to sit above data tables. No frosted glass, no glow shadows,
// no "rounded-full" capsule chips — just dense, flat pills with hairline
// borders and inline labels.

import { useEffect, useRef, useState } from "react";
import { ChevronDown, RotateCcw, X } from "lucide-react";

/* ───────────────────────── FilterBar (container) ─────────────────────────
 *
 * Wraps a row (or rows) of filter pills. Flat charcoal surface with a single
 * hairline border. Wraps on narrow viewports. The container is intentionally
 * NOT a card — no shadow, no glow — so the page hierarchy stays calm.
 */
export function FilterBar({ children, className = "" }) {
  return (
    <div
      className={`flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-md border border-sky-900/30 bg-[#0a1224]/60 px-2 py-1.5 ${className}`}
    >
      {children}
    </div>
  );
}

/* ───────────────────────── FilterDivider ─────────────────────────
 *
 * Thin vertical separator between filter groups. Hidden on small viewports
 * (filters wrap to multiple rows so the divider would look odd).
 */
export function FilterDivider() {
  return <div className="hidden md:block h-5 w-px bg-sky-900/40 mx-0.5" />;
}

/* ───────────────────────── FilterSegment ─────────────────────────
 *
 * Inline segmented control with an optional label. Right pattern when there
 * are 2-5 options the user picks from frequently (Status, Date Range).
 *
 * Buttons share borders so they read as one control, not five.
 */
export function FilterSegment({ label, value, onChange, options, className = "" }) {
  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      {label && (
        <span className="text-[11px] uppercase tracking-wider text-neutral-500 shrink-0">
          {label}
        </span>
      )}
      <div className="inline-flex rounded-md border border-sky-900/40 bg-[#06101e] overflow-hidden">
        {options.map((opt) => {
          const active = value === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => onChange(opt.key)}
              className={`px-2.5 py-1 text-[12px] leading-5 transition-colors border-r border-sky-900/40 last:border-r-0 ${
                active
                  ? "bg-sky-500/15 text-sky-100 font-medium"
                  : "text-neutral-400 hover:text-neutral-200 hover:bg-white/[0.03]"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ───────────────────────── FilterMenu ─────────────────────────
 *
 * Dropdown pill: shows "Label: <current value>" with a chevron. Clicking
 * opens a popover; the caller renders the menu contents (allowing search
 * boxes, custom rows, etc).
 *
 * Pass `children` as a function — it receives a `close()` callback you can
 * call after the user picks something.
 */
export function FilterMenu({
  label,
  value,
  placeholder = "All",
  children,
  width = "w-64",
  align = "left",
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    function onEsc(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const close = () => setOpen(false);
  const display = value || placeholder;
  const hasValue = !!value;

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 rounded-md border border-sky-900/40 bg-[#06101e] hover:bg-[#0b1426] px-2.5 py-1 text-[12px] leading-5 text-neutral-200 transition-colors"
      >
        {label && (
          <span className="text-[11px] uppercase tracking-wider text-neutral-500">
            {label}
          </span>
        )}
        <span className={hasValue ? "text-sky-100 font-medium" : "text-neutral-400"}>
          {display}
        </span>
        <ChevronDown className="h-3 w-3 text-neutral-500" />
      </button>
      {open && (
        <div
          className={`absolute z-30 mt-1.5 ${
            align === "right" ? "right-0" : "left-0"
          } ${width} max-h-72 overflow-y-auto rounded-md border border-sky-900/40 bg-[#06101e] shadow-[0_10px_28px_-10px_rgba(0,0,0,0.8)]`}
        >
          {typeof children === "function" ? children(close) : children}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── FilterMenuItem ─────────────────────────
 *
 * Convenience row for FilterMenu contents. Active state shows a sky tint.
 */
export function FilterMenuItem({ children, active, disabled, onClick, title }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={title}
      className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-[12.5px] transition-colors border-b border-sky-900/20 last:border-b-0 ${
        disabled
          ? "text-neutral-600 cursor-not-allowed"
          : active
          ? "bg-sky-500/10 text-sky-100"
          : "text-neutral-300 hover:bg-white/[0.04] hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

/* ───────────────────────── FilterInput ─────────────────────────
 *
 * Text input shaped to match the pill height. Optional label prefix.
 */
export function FilterInput({
  label,
  value,
  onChange,
  placeholder,
  width = "w-40",
}) {
  return (
    <div className="inline-flex items-center gap-1.5 rounded-md border border-sky-900/40 bg-[#06101e] px-2.5 py-1 focus-within:border-sky-500/60 transition-colors">
      {label && (
        <span className="text-[11px] uppercase tracking-wider text-neutral-500 shrink-0">
          {label}
        </span>
      )}
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`${width} bg-transparent text-[12px] leading-5 text-neutral-100 placeholder:text-neutral-600 outline-none`}
      />
    </div>
  );
}

/* ───────────────────────── FilterReset ─────────────────────────
 *
 * Discrete link-style reset button. Anchored to the end of the bar.
 */
export function FilterReset({ onClick, label = "Reset", className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 text-[12px] text-neutral-400 hover:text-sky-300 transition-colors px-1.5 py-1 ${className}`}
    >
      <RotateCcw className="h-3 w-3" />
      {label}
    </button>
  );
}

/* ───────────────────────── ActiveFilterChip ─────────────────────────
 *
 * Removable pill shown BELOW the bar when a non-default filter is active.
 * Mirrors how Datadog / Splunk show "applied filters" so the user can see
 * what's narrowing the view without scanning the pills.
 */
export function ActiveFilterChip({ label, value, onClear }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded border border-sky-500/30 bg-sky-500/10 pl-2 pr-1 py-0.5 text-[11px] text-sky-100">
      <span className="text-sky-300/80">{label}:</span>
      <span className="font-medium truncate max-w-[180px]">{value}</span>
      <button
        type="button"
        onClick={onClear}
        className="p-0.5 rounded hover:bg-sky-500/20 hover:text-white"
        aria-label={`Clear ${label}`}
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

/* ───────────────────────── ActiveFilterRow ─────────────────────────
 *
 * Convenience wrapper for the active-filter chip strip. Renders nothing
 * when there are no active filters.
 */
export function ActiveFilterRow({ chips }) {
  const visible = chips.filter(Boolean);
  if (visible.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {visible.map((c, i) => (
        <ActiveFilterChip key={i} {...c} />
      ))}
    </div>
  );
}
